import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Newspaper, Users, Video, Eye, TrendingUp, ArrowRight, Clock, User, Mail, BarChart3 } from 'lucide-react';
import { newsApi, categoryApi, reelApi, subscribeApi, contactApi, footerApi, statsApi } from '../../utils/api';
import { CATEGORY_COLOR, CATEGORY_LABEL } from '../../constants';

// API response hamesha array huna sakdaina (proxy fail/error object) — crash huna na deuna
const toArray = (v) => (Array.isArray(v) ? v : []);

function AdminDashboard() {
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [reels, setReels] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [teamCount, setTeamCount] = useState(0);
  const [viewsData, setViewsData] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [newsRes, catRes, reelRes, subRes, contactRes, footerRes, viewsRes] = await Promise.all([
        newsApi.getAll({ limit: 100 }),
        categoryApi.getAll(),
        reelApi.getAll(),
        subscribeApi.getAll(),
        contactApi.getAll(),
        footerApi.getSettings(),
        statsApi.getViews(),
      ]);
      setNews(toArray(newsRes.data));
      setCategories(toArray(catRes.data));
      setReels(toArray(reelRes.data));
      setSubscribers(toArray(subRes.data));
      setContacts(toArray(contactRes.data));
      setViewsData(Array.isArray(viewsRes.data) ? viewsRes.data : viewsRes.data || null);

      // Real team member count from footer settings
      let tCount = 0;
      try {
        const arr = JSON.parse(footerRes.data?.team_members || '[]');
        if (Array.isArray(arr)) tCount = arr.filter((m) => m && String(m.name || '').trim()).length;
      } catch { tCount = 0; }
      setTeamCount(tCount);

      // Build activity logs from all data
      const n = toArray(newsRes.data);
      const re = toArray(reelRes.data);
      const su = toArray(subRes.data);
      const co = toArray(contactRes.data);
      const activityLogs = buildActivityLogs(n, re, su, co);
      setLogs(activityLogs.slice(0, 20));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const buildActivityLogs = (news, reels, subscribers, contacts) => {
    const logs = [];
    news = toArray(news);
    reels = toArray(reels);
    subscribers = toArray(subscribers);
    contacts = toArray(contacts);
    
    // News activities
    news.forEach(n => {
      logs.push({
        type: 'news_created',
        title: n.title,
        time: n.created_at || n.date,
        icon: Newspaper,
        color: 'bg-crimson',
        action: 'समाचार प्रकाशित',
        link: '/admin/news'
      });
    });
    
    // Reel activities
    reels.forEach(r => {
      logs.push({
        type: 'reel_created',
        title: r.title,
        time: r.created_at || r.date,
        icon: Video,
        color: 'bg-gold',
        action: 'रिल प्रकाशित',
        link: '/admin/reels'
      });
    });
    
    // Subscriber activities
    subscribers.forEach(s => {
      logs.push({
        type: 'subscriber_added',
        title: s.email,
        time: s.created_at,
        icon: Mail,
        color: 'bg-blue',
        action: 'नयाँ सब्स्क्राइबर',
        link: '/admin/subscribers'
      });
    });
    
    // Contact activities
    contacts.forEach(c => {
      logs.push({
        type: 'contact_received',
        title: c.name || c.email,
        time: c.created_at,
        icon: User,
        color: 'bg-green-600',
        action: 'सम्पर्क सन्देश प्राप्त',
        link: '/admin/contacts'
      });
    });
    
    // Sort by time descending
    return logs.sort((a, b) => new Date(b.time) - new Date(a.time));
  };

  const counts = categories.map((c) => ({
    ...c,
    count: news.filter((n) => n.category_id === c.id || n.category_slug === c.slug).length,
  }));

  const maxCount = Math.max(1, ...counts.map((c) => c.count));

  const stats = [
    { label: 'कुल समाचार', value: news.length, icon: Newspaper, color: 'bg-crimson' },
    { label: 'आजको भ्यु', value: (viewsData?.today ?? 0).toLocaleString(), icon: Eye, color: 'bg-blue' },
    { label: 'रिल्स', value: reels.length, icon: Video, color: 'bg-gold' },
    { label: 'सब्स्क्राइबर', value: subscribers.length, icon: Users, color: 'bg-green-600' },
    { label: 'टिम सदस्य', value: teamCount, icon: User, color: 'bg-violet-700' },
  ];

  if (loading) {
    return <div className="text-center py-16 text-muted">लोड हुँदै...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-display text-2xl">सिंहावलोकन</h2>
      </div>
      <p className="text-[13px] text-muted mb-5">Kalo Jhola व्यवस्थापन प्यानलमा स्वागत छ।</p>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3.5 mb-6">
        {stats.map((s) => (
          <div key={s.label} className="bg-white border border-line rounded-[14px] p-4 flex items-center gap-3.5">
            <span className={`w-11 h-11 rounded-xl text-white flex items-center justify-center flex-shrink-0 ${s.color}`}>
              <s.icon size={20} />
            </span>
            <div className="min-w-0">
              <p className="font-display text-[26px] font-bold leading-none mb-1">{s.value}</p>
              <p className="text-[12.5px] font-semibold text-muted">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Site views analytics — weekly/monthly cards + daily bar graph */}
      <div className="bg-white border border-line rounded-[14px] p-5 mb-6">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <h3 className="font-display text-[17px] flex items-center gap-2">
            <BarChart3 size={16} className="text-crimson" /> वेबसाइट भ्यु विश्लेषण
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            {[
              { label: 'आज', value: viewsData?.today },
              { label: 'यो हिता', value: viewsData?.week },
              { label: 'यो महिना', value: viewsData?.month },
            ].map((v) => (
              <div key={v.label} className="bg-paper-soft border border-line rounded-xl px-3.5 py-2 text-center">
                <p className="font-display text-lg font-bold leading-none mb-0.5">{(v.value ?? 0).toLocaleString()}</p>
                <p className="text-[10.5px] font-semibold text-muted">{v.label}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="text-[11.5px] text-muted mb-2">गत १४ दिनको दैनिक भ्यु</p>
        <div className="flex items-end gap-1.5 h-36">
          {(viewsData?.daily || []).map((d) => {
            const max = Math.max(1, ...(viewsData?.daily || []).map((x) => x.views));
            const h = Math.max(4, (d.views / max) * 100);
            const dayLabel = new Date(d.date + 'T00:00:00').toLocaleDateString('ne-NP', { weekday: 'short' });
            return (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1 h-full justify-end min-w-0" title={`${d.date}: ${d.views} भ्यु`}>
                <span className="text-[9.5px] font-bold text-muted">{d.views > 0 ? d.views : ''}</span>
                <div
                  className="w-full rounded-t-md bg-gradient-to-t from-crimson/70 to-crimson hover:from-crimson-dark transition-all"
                  style={{ height: `${h}%` }}
                />
                <span className="text-[9.5px] text-muted truncate w-full text-center">{dayLabel}</span>
              </div>
            );
          })}
          {(!viewsData?.daily || viewsData.daily.length === 0) && (
            <p className="text-[13px] text-muted">अझै भ्यु डाटा छैन।</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 bg-white border border-line rounded-[14px] p-5">
          <h3 className="font-display text-[17px] mb-4 flex items-center gap-2">
            <TrendingUp size={16} className="text-crimson" /> श्रेणी अनुसार समाचार
          </h3>
          <div className="flex flex-col gap-3">
            {counts.map((c) => (
              <div key={c.id}>
                <div className="flex items-center justify-between text-[13px] mb-1">
                  <span className="font-semibold text-ink-soft">{c.name}</span>
                  <span className="font-bold">{c.count}</span>
                </div>
                <div className="h-2 rounded-full bg-paper-tint overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${(c.count / maxCount) * 100}%`, background: CATEGORY_COLOR[c.slug] || '#2457A8' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-line rounded-[14px] p-5">
          <h3 className="font-display text-[17px] mb-4 flex items-center gap-2">
            <Clock size={16} className="text-crimson" /> हालको गतिविधि
          </h3>
          <div className="flex flex-col gap-3 max-h-[300px] overflow-y-auto pr-2">
            {logs.map((log, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-paper-soft hover:bg-paper-tint transition-colors cursor-pointer" onClick={() => navigate(log.link)}>
                <span className={`w-8 h-8 rounded-lg text-white flex items-center justify-center flex-shrink-0 ${log.color}`}>
                  <log.icon size={15} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-semibold text-ink truncate">{log.action}</p>
                  <p className="text-[12px] text-muted truncate">{log.title}</p>
                  <p className="text-[11px] text-muted mt-1">
                    {log.time ? new Date(log.time).toLocaleString('ne-NP') : ''}
                  </p>
                </div>
              </div>
            ))}
            {logs.length === 0 && <p className="text-[13px] text-muted">कुनै गतिविधि छैन।</p>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border border-line rounded-[14px] p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-[17px] flex items-center gap-2">
              <Mail size={16} className="text-crimson" /> ताजा सम्पर्क सन्देश
            </h3>
            <button className="text-[12.5px] font-semibold text-crimson flex items-center gap-1 hover:underline" onClick={() => navigate('/admin/contacts')}>
              सबै हेर्नुहोस् <ArrowRight size={14} />
            </button>
          </div>
          <div className="flex flex-col gap-2.5 max-h-[340px] overflow-y-auto pr-1">
            {contacts.slice(0, 6).map((c, i) => (
              <div key={c.id || i} className="border border-line rounded-xl p-3 hover:bg-paper-soft cursor-pointer" onClick={() => navigate('/admin/contacts')}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className="text-[13px] font-semibold text-ink truncate">{c.name || 'बेनामी'}</p>
                  {c.created_at && <span className="text-[10.5px] text-muted flex-shrink-0">{new Date(c.created_at).toLocaleDateString('ne-NP')}</span>}
                </div>
                <p className="text-[12.5px] text-muted line-clamp-2">{c.message}</p>
              </div>
            ))}
            {contacts.length === 0 && <p className="text-[13px] text-muted">कुनै सन्देश छैन।</p>}
          </div>
        </div>

        <div className="bg-white border border-line rounded-[14px] p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-[17px]">हालैका समाचार</h3>
            <button className="text-[12.5px] font-semibold text-crimson flex items-center gap-1 hover:underline" onClick={() => navigate('/admin/news')}>
              सबै हेर्नुहोस् <ArrowRight size={14} />
            </button>
          </div>
          <div className="flex flex-col gap-2.5">
            {news.slice(0, 6).map((n) => (
              <div key={n.id} className="flex items-center gap-3 border border-line rounded-xl p-2.5 hover:bg-paper-soft cursor-pointer" onClick={() => navigate('/admin/news')}>
                <img
                  src={n.image || 'https://via.placeholder.com/80x80?text=K'}
                  alt=""
                  className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-[13.5px] font-semibold truncate">{n.title}</p>
                  <span className="text-xs text-muted">
                    {CATEGORY_LABEL[n.category_slug] || n.category_name} • {n.author}
                  </span>
                </div>
                <span className="text-[11.5px] font-semibold text-muted flex-shrink-0">{n.views || 0} भ्यु</span>
              </div>
            ))}
            {news.length === 0 && <p className="text-[13px] text-muted">कुनै समाचार छैन।</p>}
          </div>
        </div>
      </div>

      <div className="bg-ink text-white rounded-[14px] p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <p className="font-display text-lg font-bold mb-1">नयाँ समाचार प्रकाशित गर्नुहोस्</p>
          <p className="text-[13px] opacity-70">फोटो, भिडियो र ग्यालरीसहित समाचार तुरुन्तै साइटमा देखिनेछ।</p>
        </div>
        <button
          className="bg-crimson text-white font-semibold text-[13.5px] px-5 py-2.5 rounded-full hover:bg-crimson-dark whitespace-nowrap"
          onClick={() => navigate('/admin/news')}
        >
          + नयाँ थप्नुहोस्
        </button>
      </div>
    </div>
  );
}

export default AdminDashboard;